import { Check } from "lucide-react";
import { motion, useReducedMotion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { useMemo } from "react";

const tiers = [
  {
    name: "CRM & Systems Setup",
    price: "$2,500",
    priceNote: "/mo",
    description: "We set up your CRM and build the automations that run your intake. Every new lead gets followed up fast — automatically. Built for immigration firms that want a smarter system, with the same team.",
    features: ["CRM setup", "Automated lead follow-up (email + SMS)", "Appointment reminders", "Intake workflow automation", "Lead tracking dashboard"],
    cta: "Get Started",
    highlighted: false,
  },
  {
    name: "Full Pipeline System",
    price: "$3,500",
    priceNote: "/mo",
    description: "Everything in CRM & Systems, plus a full paid ad campaign on either Meta or Google Ads. We bring the leads in and make sure they convert — so your calendar fills with real consultations.",
    features: ["Everything in CRM & Systems Setup", "Paid ad campaign (Meta OR Google Ads — one platform)", "Ad creative and copy", "Campaign management and weekly optimization", "Monthly performance reports"],
    cta: "Get Started",
    highlighted: true,
  },
  {
    name: "Enterprise",
    price: "Custom",
    priceNote: "",
    description: "Built around your firm. Priced around your needs.",
    features: ["Unlimited ad account management", "Custom AI integrations", "AI-powered client chatbot", "Professional website build", "Dedicated strategy and support"],
    cta: "Let's Talk",
    highlighted: false,
  },
];

const cardInitial = { opacity: 0, y: 30 };
const cardAnimate = { opacity: 1, y: 0 };

export function Services() {
  const reduced = useReducedMotion();
  const navigate = useNavigate();
  const transitions = useMemo(
    () => tiers.map((_, i) => (reduced ? {} : { delay: i * 0.15, duration: 0.5 })),
    [reduced]
  );

  return (
    <section id="services" className="py-8 md:py-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-8">
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-foreground">Simple plans. Real results.</h2>
          <p className="mt-2 text-lg text-muted-foreground">Pick the plan that fits your firm. Scale when you're ready.</p>
        </div>

        <div className="grid md:grid-cols-3 gap-4">
          {tiers.map((tier, i) => (
            <motion.div
              key={tier.name}
              initial={reduced ? {} : cardInitial}
              whileInView={reduced ? {} : cardAnimate}
              viewport={{ once: true, amount: 0.2 }}
              transition={transitions[i]}
              className={`rounded-2xl p-6 flex flex-col will-change-transform ${
                tier.highlighted
                  ? "bg-primary text-primary-foreground relative overflow-hidden"
                  : "border border-border bg-card"
              }`}
            >
              {tier.highlighted && (
                <span className="md:absolute md:top-4 md:right-4 bg-primary-foreground text-primary text-xs font-bold px-3 py-1 rounded-full inline-flex items-center gap-1 self-start mb-2 md:mb-0">
                  <Check className="w-3 h-3" /> Most Popular
                </span>
              )}
              <h3 className="text-xl font-semibold">{tier.name}</h3>
              <p className="text-3xl font-bold mt-3">
                {tier.price}
                {tier.priceNote && (
                  <span className={`text-base font-normal ${tier.highlighted ? "opacity-80" : "text-muted-foreground"}`}>{tier.priceNote}</span>
                )}
              </p>
              <p className={`mt-3 text-sm leading-relaxed flex-1 ${tier.highlighted ? "opacity-90" : "text-muted-foreground"}`}>{tier.description}</p>
              <ul className="mt-4 space-y-2">
                {tier.features.map((f) => (
                  <li key={f} className="flex items-start gap-2 text-sm">
                    <Check className={`w-4 h-4 mt-0.5 shrink-0 ${tier.highlighted ? "text-primary-foreground" : "text-primary"}`} />
                    <span>{f}</span>
                  </li>
                ))}
              </ul>
              <motion.button
                whileHover={reduced ? {} : { scale: 1.03 }}
                whileTap={reduced ? {} : { scale: 0.97 }}
                className={`mt-6 font-semibold py-3 px-6 rounded-full transition-colors w-full sm:w-auto ${
                  tier.highlighted
                    ? "bg-primary-foreground text-primary hover:opacity-90"
                    : "border-2 border-primary text-primary hover:bg-primary/5"
                }`}
                onClick={() => navigate("/audit")}
              >
                {tier.cta}
              </motion.button>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
